import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Inbox, RefreshCw, Mail, Phone, Globe } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/components/Seo";

export default function Admin() {
  useSeo({ path: "/admin", title: "Leads Dashboard", description: "Hays Holdings admin dashboard.", robots: "noindex, nofollow" });
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/leads"), api.get("/leads/stats")])
      .then(([l, s]) => {
        setLeads(l.data);
        setStats(s.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user === null) return;
    if (user === false) {
      navigate("/login");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (user === null || user === false) {
    return <div className="flex min-h-screen items-center justify-center bg-surface text-sm text-foreground/60">Loading...</div>;
  }

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-surface" data-testid="admin-page">
      <header className="border-b border-border bg-white">
        <div className="container-pn flex h-16 items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center bg-navy font-heading text-base font-black text-white">HH</span>
            <span className="font-heading text-base font-extrabold tracking-tight text-navy">Partnership Leads</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={load} data-testid="admin-refresh" className="flex items-center gap-2 text-sm font-medium text-foreground/70 hover:text-brand">
              <RefreshCw size={16} /> Refresh
            </button>
            <button onClick={handleLogout} data-testid="admin-logout" className="flex items-center gap-2 bg-navy px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="container-pn py-10">
        <div className="grid grid-cols-3 gap-px overflow-hidden border border-border bg-border">
          <div className="bg-white p-6">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-foreground/50">Total Requests</p>
            <p className="mt-2 font-heading text-3xl font-black text-navy" data-testid="stat-total">{stats?.total ?? "—"}</p>
          </div>
          <div className="bg-white p-6">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-foreground/50">New</p>
            <p className="mt-2 font-heading text-3xl font-black text-orange" data-testid="stat-new">{stats?.new ?? "—"}</p>
          </div>
          <div className="bg-white p-6">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-foreground/50">Companies</p>
            <p className="mt-2 font-heading text-3xl font-black text-navy" data-testid="stat-companies">{stats?.distinct_companies ?? "—"}</p>
          </div>
        </div>

        <div className="mt-8 border border-border bg-white">
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <h2 className="font-heading text-lg font-bold text-navy">Partnership Requests</h2>
            <span className="text-sm text-foreground/60">{leads.length} total</span>
          </div>

          {loading ? (
            <div className="px-6 py-16 text-center text-sm text-foreground/60">Loading requests...</div>
          ) : leads.length === 0 ? (
            <div className="flex flex-col items-center px-6 py-16 text-center" data-testid="admin-empty">
              <Inbox size={40} className="text-foreground/30" />
              <p className="mt-4 text-sm text-foreground/60">No partnership requests yet. Submissions will appear here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="leads-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Channels</TableHead>
                    <TableHead>Goals</TableHead>
                    <TableHead>Received</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((l) => (
                    <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                      <TableCell className="font-semibold text-navy">{l.name}</TableCell>
                      <TableCell className="text-foreground/70">
                        {l.company || "—"}
                        {l.website && (
                          <a href={l.website} target="_blank" rel="noopener noreferrer" className="mt-0.5 flex items-center gap-1 text-xs text-brand hover:underline"><Globe size={11} /> site</a>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 text-xs text-foreground/70">
                          <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 hover:text-brand"><Mail size={12} /> {l.email}</a>
                          {l.phone && <span className="flex items-center gap-1.5"><Phone size={12} /> {l.phone}</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        {l.product_category ? (
                          <Badge className="border-0 bg-brand/10 font-medium text-brand">{l.product_category}</Badge>
                        ) : "—"}
                      </TableCell>
                      <TableCell className="max-w-[12rem] text-sm text-foreground/70"><span className="line-clamp-2">{l.current_channels || "—"}</span></TableCell>
                      <TableCell className="max-w-xs text-sm text-foreground/70"><span className="line-clamp-2">{l.partnership_goals || l.message || "—"}</span></TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-foreground/60">
                        {new Date(l.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
